import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faCheck, faTrash, faXmark, faSpinner } from '@fortawesome/free-solid-svg-icons';

import StockModal from './StockModal';
import { exactTime } from './stockShared';

/**
 * The pictures of a delivery.
 *
 * THE PHOTO IS THE POINT. A delivery that turns up short, or with two crushed
 * boxes, is a conversation with a supplier three days later, and that
 * conversation is won or lost on whether anybody photographed it on the
 * trolley.
 *
 * FILED AGAINST THE ORDER. A photo is of a DELIVERY — a trolley, a pallet, a
 * crushed box — and a delivery is a purchase arriving, not one line of it.
 * Filing per line would ask the warehouse to photograph the same pallet once
 * for every item on it.
 *
 * ONE DIALOG, NOT TWO. Receiving a delivery and photographing it are the same
 * moment standing in the same doorway, so the pictures, the note and the
 * confirmation are one screen — asking for the note in a second dialog after
 * the first is how the note stops being written.
 *
 * The same dialog, in 'photos' mode, adds pictures to a delivery already
 * recorded; then there is nothing left to confirm and it only saves files.
 */
export default function DeliverModal({ order, mode = 'receive', canRemove, onClose, onDone }) {
    // 'receive' — the van is here: pictures AND the note in one dialog, then
    //             the order flips. 'photos' — adding to a delivery already
    //             recorded, so there is nothing left to confirm.
    const receiving = mode === 'receive';
    const [picked, setPicked] = useState([]);      // File[]
    const [previews, setPreviews] = useState([]);  // object URLs
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [progress, setProgress] = useState('');
    const [error, setError] = useState('');

    const existing = order.photos || [];

    /* MANDATORY ON RECEIVING. The server refuses a delivery with no picture,
       so the button waits for one rather than letting somebody fill the dialog
       in and be told no. Already-filed pictures count — adding a second one
       later is not the point. */
    const hasPicture = existing.length > 0 || picked.length > 0;

    // Object URLs are a memory leak if they are never revoked.
    useEffect(() => {
        const urls = picked.map((f) => URL.createObjectURL(f));
        setPreviews(urls);
        return () => urls.forEach(URL.revokeObjectURL);
    }, [picked]);

    const addFiles = (fileList) => {
        const incoming = [...(fileList || [])].filter((f) => f.type.startsWith('image/'));
        const tooBig = incoming.find((f) => f.size > 25 * 1024 * 1024);
        if (tooBig) { setError(`${tooBig.name} is over 25 MB.`); return; }
        setError('');
        setPicked((p) => [...p, ...incoming].slice(0, 10));
    };

    const uploadAll = async () => {
        for (let i = 0; i < picked.length; i += 1) {
            const file = picked[i];
            setProgress(`Uploading picture ${i + 1} of ${picked.length}…`);

            const presign = await axios.post(`/api/stock/orders/${order.id}/photo-presign`, {
                file_name: file.name,
                content_type: file.type || 'image/jpeg',
            });

            // withCredentials off: the bucket's CORS sends no Allow-Credentials,
            // so the browser blocks a credentialed PUT.
            await axios.put(presign.data.url, file, {
                withCredentials: false,
                headers: { 'Content-Type': file.type || 'image/jpeg' },
            });

            await axios.post(`/api/stock/orders/${order.id}/photo`, {
                file_url: presign.data.file_url,
                file_name: file.name,
                file_size: file.size,
            });
        }
        setProgress('');
    };

    const submit = async (e) => {
        e.preventDefault();
        if (saving) return;
        setSaving(true);
        setError('');
        try {
            // PICTURES FIRST, STATUS SECOND. If an upload fails the order is
            // still on the way and the whole thing can simply be done again;
            // the other order — flip, then lose the photos — leaves a received
            // delivery nobody can evidence.
            await uploadAll();
            if (receiving) {
                setProgress('Marking received…');
                await axios.post(`/api/stock/orders/${order.id}/receive`, {
                    note: note.trim() || null,
                });
            }
            await onDone();
        } catch (err) {
            setError(err.response?.data?.error || 'That did not save. Try again.');
            setProgress('');
            setSaving(false);
        }
    };

    const removeExisting = async (photoId) => {
        try {
            await axios.delete(`/api/stock/orders/photos/${photoId}`);
            await onDone({ keepOpen: true });
        } catch (err) {
            setError(err.response?.data?.error || 'Could not remove that picture.');
        }
    };

    return (
        <StockModal
            title={receiving ? `It arrived — order #${order.id}` : `Delivery pictures — order #${order.id}`}
            subtitle={`${(order.lines || []).length} item${(order.lines || []).length === 1 ? '' : 's'}`
                + `${order.reference ? ` · ref ${order.reference}` : ''}`}
            onClose={onClose}
            width={560}
        >
            <form onSubmit={submit}>
                {receiving && (
                    <div className="stk__orderLines">
                        {(order.lines || [])
                            .filter((l) => l.status === 'ordered')
                            .map((l) => (
                                <div key={l.request_id} className="stk__orderLine">
                                    {l.quantity ? <span className="stk__orderQty">{l.quantity}×</span> : null}
                                    <span className="stk__orderName">{l.item_name}</span>
                                    <span className="stk__orderCat">{l.category_name}</span>
                                </div>
                            ))}
                    </div>
                )}

                {existing.length > 0 && (
                    <div className="stk__field">
                        <span className="stk__fieldLabel">Already on this delivery</span>
                        <div className="stk__thumbs">
                            {existing.map((p) => (
                                <span key={p.id} className="stk__thumb">
                                    <a href={p.file_url} target="_blank" rel="noopener noreferrer">
                                        <img
                                            src={p.file_url}
                                            alt={p.file_name || 'Delivery'}
                                            loading="lazy"
                                        />
                                    </a>
                                    <span className="stk__thumbMeta" title={exactTime(p.uploaded_at)}>
                                        {p.uploaded_by_name || 'Added'}
                                    </span>
                                    {canRemove && (
                                        <button
                                            type="button"
                                            className="stk__thumbRemove"
                                            onClick={() => removeExisting(p.id)}
                                            aria-label="Remove this picture"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    )}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                <div className="stk__field">
                    <span className="stk__fieldLabel">
                        <FontAwesomeIcon icon={faCamera} /> Pictures of the delivery
                        {receiving ? <span className="stk__req">required</span> : null}
                    </span>

                    <label className={`stk__camera ${receiving && !hasPicture ? 'is-required' : ''}`}>
                        <FontAwesomeIcon icon={faCamera} />
                        <span className="stk__cameraMain">
                            {receiving ? 'Take a picture of the delivery' : 'Take or choose pictures'}
                        </span>
                        <span className="stk__cameraHint">
                            {receiving && !hasPicture
                                ? 'Required — at least one. Add several if you like: the boxes, the label, anything damaged.'
                                : 'You can add several — the boxes, the label, anything damaged.'}
                        </span>
                        <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                        />
                    </label>

                    {picked.length > 0 && (
                        <div className="stk__thumbs stk__thumbs--new">
                            {picked.map((file, idx) => (
                                <span key={`${file.name}-${idx}`} className="stk__thumb">
                                    <img src={previews[idx]} alt={file.name} />
                                    <button
                                        type="button"
                                        className="stk__thumbRemove"
                                        onClick={() => setPicked((p) => p.filter((_, i) => i !== idx))}
                                        aria-label={`Remove ${file.name}`}
                                    >
                                        <FontAwesomeIcon icon={faXmark} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {receiving && (
                    <label className="stk__field">
                        <span className="stk__fieldLabel">Anything wrong with it? (optional)</span>
                        <textarea
                            className="stk__textarea"
                            rows={2}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="e.g. 1 box crushed, 11 usable"
                        />
                    </label>
                )}

                {progress ? <p className="stk__progress">{progress}</p> : null}
                {error ? <p className="stk__formError">{error}</p> : null}

                <div className="stk__modalActions">
                    <button type="button" className="stk__btn stk__btn--ghost" onClick={onClose}>
                        Close
                    </button>
                    <button
                        type="submit"
                        className="stk__btn stk__btn--ok"
                        disabled={saving || !hasPicture}
                    >
                        <FontAwesomeIcon icon={saving ? faSpinner : faCheck} spin={saving} />{' '}
                        {saving
                            ? 'Saving…'
                            : receiving
                                ? 'Mark received'
                                : `Save ${picked.length} picture${picked.length === 1 ? '' : 's'}`}
                    </button>
                </div>
            </form>
        </StockModal>
    );
}
